// /var/www/speakiq/frontend/src/pages/student/SchoolIDCard.js
import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import QRCode from 'react-qr-code';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981',
  warning: '#F59E0B', error: '#EF4444', grey: '#6B7280', border: '#E5E7EB',
};

function cefrBg(level) {
  if (!level) return C.grey;
  const l = level.toUpperCase();
  if (l === 'C1' || l === 'C2') return '#D97706';
  if (l === 'B1' || l === 'B2') return C.teal;
  return '#64748B';
}

const PACKAGE_LABELS = {
  ai_basic: 'AI Practice Only',
  ai_ielts: 'AI IELTS Prep',
  ai_business: 'AI Business English',
  starter_group: 'Group Classes',
  standard: 'Standard',
  premium: 'Premium Group',
  ielts_intensive: 'IELTS Group Intensive',
  corporate: 'Corporate Group',
  online_group: 'Online Group Classes',
  online_standard: 'Online Standard',
  online_1on1: 'Online Premium Group',
  online_ielts: 'Online IELTS Group',
  trial: 'Trial',
};

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
}

function RealQRCode({ value }) {
  return (
    <div style={{ background: '#fff', padding: 10, borderRadius: 8, border: '2px solid #E5E7EB', display: 'inline-block' }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2, color: '#6B7280', marginBottom: 6, textAlign: 'center' }}>SCAN TO VERIFY</div>
      <QRCode value={value} size={110} fgColor="#0D1B2A" bgColor="#fff" />
    </div>
  );
}

export default function SchoolIDCard() {
  const [cardData, setCardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    api.get('/api/student/my-id')
      .then(res => setCardData(res.data))
      .catch(err => {
        const status = err.response?.status;
        if (status === 404 || status === 403) {
          setCardData(null);
        } else {
          setError('Failed to load your ID card. Please try again later.');
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const primaryColour = cardData?.tenant?.primary_colour || C.navy;
  const schoolName = cardData?.tenant?.name || 'Your School';
  const idNumber = cardData?.id_number || '';
  const verifyUrl = `https://speakiq.uk/verify-id/${idNumber}`;
  const isActive = cardData?.status === 'active';

  const cardStyle = {
    width: 340, height: 210, borderRadius: 14,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    cursor: 'pointer',
    position: 'relative',
    transformStyle: 'preserve-3d',
    transition: 'transform 0.6s cubic-bezier(.4,2,.6,1)',
    transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
  };

  const faceCss = {
    position: 'absolute', width: '100%', height: '100%',
    borderRadius: 14, backfaceVisibility: 'hidden', overflow: 'hidden',
  };

  if (loading) return (
    <Layout role="student">
      <div style={{ textAlign: 'center', padding: 80, color: C.grey }}>Loading your ID card…</div>
    </Layout>
  );

  if (error) return (
    <Layout role="student">
      <div style={{ textAlign: 'center', padding: 80, color: C.error }}>{error}</div>
    </Layout>
  );

  const noCard = !cardData || !idNumber;

  return (
    <Layout role="student">
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '32px 16px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: C.navy, marginBottom: 4 }}>My School ID</h1>
        <p style={{ color: C.grey, fontSize: 14, marginBottom: 28 }}>Your digital identity card</p>

        {noCard ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', background: '#F9FAFB', borderRadius: 14, border: '2px dashed ' + C.border }}>
            <div style={{ fontSize: 52, marginBottom: 12 }}>🎓</div>
            <p style={{ fontWeight: 600, color: C.grey, marginBottom: 6 }}>Your School ID will be issued at your first visit to the centre.</p>
            <p style={{ fontSize: 13, color: C.grey }}>Once issued, your ID card will appear here.</p>
          </div>
        ) : (
          <>
            <div style={{ perspective: 800, display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <div style={cardStyle} onClick={() => setFlipped(f => !f)}>

                {/* FRONT */}
                <div style={{ ...faceCss, background: '#F0F4F8' }}>
                  {/* Top band */}
                  <div style={{ background: primaryColour, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px', position: 'relative' }}>
                    <span style={{ color: '#fff', fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>SpeakIQ</span>
                    <span style={{ color: '#fff', fontSize: 12, fontWeight: 600, maxWidth: 160, textAlign: 'right', lineHeight: 1.2 }}>{schoolName}</span>
                  </div>

                  {/* Photo circle */}
                  <div style={{ position: 'absolute', top: 28, left: 16, width: 64, height: 64, borderRadius: '50%', border: '3px solid #fff', overflow: 'hidden', background: primaryColour, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                    {cardData.photo_url
                      ? <img src={cardData.photo_url} alt="ID" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ color: '#fff', fontWeight: 700, fontSize: 20 }}>{initials(cardData.full_name)}</span>}
                  </div>

                  {/* Info */}
                  <div style={{ padding: '8px 14px 0 90px' }}>
                    <div style={{ fontWeight: 700, fontSize: 16, color: C.navy, marginBottom: 2, lineHeight: 1.2 }}>{cardData.full_name}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 14, color: C.teal, marginBottom: 6, letterSpacing: 1 }}>{idNumber}</div>
                    <div style={{ display:'flex', gap:4, flexWrap:'wrap', alignItems:'center' }}>
                      {cardData.cefr_level && (
                        <span style={{ background: cefrBg(cardData.cefr_level), color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>{cardData.cefr_level}</span>
                      )}
                      {cardData.package && (
                        <span style={{ background: '#F0FDF4', color: '#166534', fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, border: '1px solid #BBF7D0' }}>
                          {PACKAGE_LABELS[cardData.package] || cardData.package}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={{ padding: '6px 14px 0' }}>
                    <span style={{ fontSize: 11, color: C.grey, marginRight: 12 }}>
                      Enrolled: <strong>{cardData.enrolled_date ? new Date(cardData.enrolled_date).toLocaleDateString('en-GB') : '—'}</strong>
                    </span>
                    <span style={{ fontSize: 11, color: C.grey }}>
                      Valid until: <strong>{cardData.valid_until ? new Date(cardData.valid_until).toLocaleDateString('en-GB') : '—'}</strong>
                    </span>
                  </div>

                  {/* Bottom band */}
                  <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.18)', padding: '5px 14px' }}>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.85)', letterSpacing: 1 }}>Powered by SpeakIQ</span>
                  </div>
                </div>

                {/* BACK */}
                <div style={{ ...faceCss, background: '#F0F4F8', transform: 'rotateY(180deg)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 16 }}>
                  <RealQRCode value={verifyUrl} />
                  <div style={{ fontSize: 10, color: C.grey, textAlign: 'center' }}>
                    <div style={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{verifyUrl}</div>
                    {cardData.tenant?.address && <div style={{ marginTop: 4 }}>{cardData.tenant.address}</div>}
                    <div style={{ marginTop: 4, fontStyle: 'italic' }}>This card is property of {schoolName}</div>
                  </div>
                </div>

              </div>
            </div>

            <p style={{ textAlign: 'center', fontSize: 12, color: C.grey, marginBottom: 20 }}>Tap card to flip</p>

            {/* Status badge */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
              <span style={{ background: isActive ? '#D1FAE5' : '#FEE2E2', color: isActive ? C.success : C.error, fontWeight: 700, fontSize: 13, padding: '4px 18px', borderRadius: 20, letterSpacing: 1 }}>
                {isActive ? 'ACTIVE' : 'INACTIVE'}
              </span>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 20 }}>
              <button
                onClick={() => alert('Digital card PDF will be available soon')}
                style={{ background: C.navy, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Download Card
              </button>
              <button
                onClick={() => window.open(verifyUrl, '_blank')}
                style={{ background: C.teal, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                Verify this card
              </button>
            </div>

            {/* Info box */}
            <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#1E40AF', lineHeight: 1.6 }}>
              Your School ID is your identity at {schoolName}. Present it at reception when visiting.
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
